import {
  CURRENCY_DISPLAYS,
  type CurrencyDisplay,
  DENSITIES,
  DENSITY_ROW_HEIGHT_PX,
  type Density,
  paletteForMode,
  THEME_MODES,
  type ThemeMode,
} from '@observatory/ui-design';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

// Preferences are persisted client-side (localStorage). v1 deliberately
// does not push them to Postgres: there is no documented user-profile RPC
// in SPEC §7.1, and ADR-0010 §"Trigger to revisit" carves out genuinely
// UI-only state (column-order memory, preferences blob) from the
// documented-surface rule. If/when a server-side profile surface lands,
// add a one-line sync here.

export interface Preferences {
  readonly theme: ThemeMode;
  readonly density: Density;
  readonly currency: CurrencyDisplay;
}

const DEFAULT_PREFERENCES: Preferences = {
  theme: 'light',
  density: 'compact',
  currency: 'eur-primary',
};

const STORAGE_KEY = 'observatory.preferences.v1';

function readStored(): Preferences {
  if (typeof window === 'undefined') return DEFAULT_PREFERENCES;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFERENCES;
    const parsed = JSON.parse(raw) as Partial<Preferences>;
    return {
      theme: THEME_MODES.includes(parsed.theme as ThemeMode)
        ? (parsed.theme as ThemeMode)
        : DEFAULT_PREFERENCES.theme,
      density: DENSITIES.includes(parsed.density as Density)
        ? (parsed.density as Density)
        : DEFAULT_PREFERENCES.density,
      currency: CURRENCY_DISPLAYS.includes(parsed.currency as CurrencyDisplay)
        ? (parsed.currency as CurrencyDisplay)
        : DEFAULT_PREFERENCES.currency,
    };
  } catch {
    return DEFAULT_PREFERENCES;
  }
}

interface PreferencesContextValue {
  readonly preferences: Preferences;
  setTheme(theme: ThemeMode): void;
  setDensity(density: Density): void;
  setCurrency(currency: CurrencyDisplay): void;
}

const PreferencesContext = createContext<PreferencesContextValue | null>(null);

export function PreferencesProvider({ children }: { readonly children: ReactNode }) {
  const [preferences, setPreferences] = useState<Preferences>(() => readStored());

  // Apply theme + density to <html> as data attributes + CSS variables so
  // every component picks them up via plain CSS (no React subscriptions in
  // every cell). Update on each preferences change.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const systemPrefersDark =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches;
    const palette = paletteForMode(preferences.theme, systemPrefersDark);
    const resolved =
      preferences.theme === 'system' ? (systemPrefersDark ? 'dark' : 'light') : preferences.theme;
    root.setAttribute('data-theme', resolved);
    root.setAttribute('data-theme-preference', preferences.theme);
    root.setAttribute('data-density', preferences.density);
    root.setAttribute('data-currency', preferences.currency);
    root.style.setProperty('--color-app-bg', palette.bg);
    root.style.setProperty('--color-app-fg', palette.fg);
    root.style.setProperty('--color-app-muted', palette.muted);
    root.style.setProperty('--color-app-border', palette.border);
    root.style.setProperty('--color-app-soft', palette.soft);
    root.style.setProperty('--color-app-accent', palette.accent);
    root.style.setProperty('--row-height', `${DENSITY_ROW_HEIGHT_PX[preferences.density]}px`);
    root.style.colorScheme = resolved;
  }, [preferences]);

  // Persist on every change. Wrap in try/catch so a full localStorage
  // (quota / private-mode) never blocks the in-memory state update.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    } catch {
      // ignored: preference still applied in-memory for this session
    }
  }, [preferences]);

  const update = useCallback((patch: Partial<Preferences>) => {
    setPreferences((prev) => ({ ...prev, ...patch }));
  }, []);

  const value = useMemo<PreferencesContextValue>(
    () => ({
      preferences,
      setTheme: (theme) => update({ theme }),
      setDensity: (density) => update({ density }),
      setCurrency: (currency) => update({ currency }),
    }),
    [preferences, update],
  );

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences(): PreferencesContextValue {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error('usePreferences must be used inside <PreferencesProvider>');
  return ctx;
}
